/**
 * D284: how everything on the world looks, in one place, for both renderers. The flat map
 * (`ui/iso.ts`, `ui/iso-paint.ts`) and the 3D world (`idle/three/build.ts`) read their
 * colours, breeds, building tiers, crop looks, festival marks, trees, water, flowers, bales
 * and hedges from here, so the two cannot drift: a cow is the same breed in both, wheat is
 * gold when ripe in both.
 *
 * Pure data and small pure rules, so either renderer can take it without pulling in the
 * other. Which breed and which building a tier brings, and the ids they go by, are the
 * data's (`collection` in `packages/data`); how each one looks is here. Ground and building materials that re-theme with the stylesheet stay
 * on the palette (`palette` in `ui/iso.ts`, from the `--map-*` tokens); what lives here is
 * every colour the painters used to hold as a literal, and how each is mixed from the palette
 * where it is mixed. A mix is written `[colour, amount]`, the arguments of `mix`.
 */

import { getIdleContent } from '@hedgerow/data';

/** A colour and how far towards it a base colour is mixed: `mix(base, c, t)`. */
export type Mix = readonly [string, number];

/** The soft shadow every standing thing on the flat map casts. */
export const SHADOW = 'rgba(42,36,24,.22)';

/* ---------- animals (D240, D268) ---------- */

export type AnimalKind = 'cow' | 'sheep' | 'pig' | 'hen';

/** D268: what each breed looks like, from the first a plot has to the best, by kind. */
export interface Breed {
  hide: string;
  /** Patches over the back. */
  spots?: string;
  /** A band round the middle. */
  belt?: string;
  head?: string;
  /** A hen's speckles. */
  speck?: string;
}

/** How each breed in the data looks, by its id. */
export const BREED_LOOK: Record<string, Breed> = {
  // Brown, white, speckled, golden.
  brown_hen: { hide: '#A8673A' },
  white_hen: { hide: '#F7F3EA' },
  speckled_hen: { hide: '#EFE8DA', speck: '#3A3330' },
  golden_hen: { hide: '#E3B04B' },
  // A brown cow, the Friesian, the Belted Galloway, the golden Jersey.
  brown_cow: { hide: '#8A5A3A', head: '#6E4529' },
  friesian: { hide: '#F4F1E8', spots: '#3E3831', head: '#3E3831' },
  belted_galloway: { hide: '#2F2A26', belt: '#F4F1E8', head: '#2F2A26' },
  jersey: { hide: '#D9A55A', head: '#B98236' },
  // Grey, white, the black-faced Suffolk, a golden fleece.
  grey_sheep: { hide: '#C9C2B0', head: '#6A6256' },
  white_sheep: { hide: '#F2EEE2', head: '#4A443C' },
  suffolk: { hide: '#F2EEE2', head: '#1F1B17' },
  golden_fleece: { hide: '#E8C77A', head: '#4A443C' },
  // Pink, the Gloucester Old Spot, the Saddleback, a golden one.
  pink_pig: { hide: '#E9AFA8' },
  gloucester_old_spot: { hide: '#F0D6CC', spots: '#3A3330' },
  saddleback: { hide: '#3A3330', belt: '#F0D6CC' },
  golden_pig: { hide: '#E6B66A' },
};

/** The producer whose milestones bring each kind of animal its breeds. */
const ANIMAL_PRODUCER: Record<AnimalKind, string> = {
  hen: 'hens',
  cow: 'cows',
  sheep: 'sheep',
  pig: 'pigs',
};

/** One kind's breeds in the data, first to best, with the milestones each needs. */
function breedsInData(kind: AnimalKind): Array<{ id: string; tier: number }> {
  return getIdleContent()
    .collection.breeds.filter((b) => b.producer === ANIMAL_PRODUCER[kind])
    .sort((a, b) => a.tier - b.tier);
}

/** D268: what each kind's breeds look like, from the first a plot has to the best. */
export const BREEDS: Record<AnimalKind, Breed[]> = {
  hen: breedsInData('hen').map((b) => BREED_LOOK[b.id] ?? { hide: '#F7F3EA' }),
  cow: breedsInData('cow').map((b) => BREED_LOOK[b.id] ?? { hide: '#F4F1E8' }),
  sheep: breedsInData('sheep').map((b) => BREED_LOOK[b.id] ?? { hide: '#EFEAD9' }),
  pig: breedsInData('pig').map((b) => BREED_LOOK[b.id] ?? { hide: '#E9AFA8' }),
};

/** An animal drawn with no breed: the old game's stock, before D268. */
export const PLAIN_ANIMAL: Record<AnimalKind, Breed> = {
  hen: { hide: '#F7F3EA' },
  cow: { hide: '#F4F1E8', spots: '#3E3831', head: '#3E3831' },
  pig: { hide: '#E9AFA8' },
  sheep: { hide: '#EFEAD9', head: '#4A443C' },
};

/** A hen's comb, and her beak and legs. */
export const HEN = { comb: '#C8473A', beak: '#D99A2B' } as const;

/** D268: which of its kind's breeds an animal takes from the milestones reached, by the data. */
export function breedOf(tier: number, kind: AnimalKind): number {
  return Math.max(0, breedsInData(kind).filter((b) => b.tier <= tier).length - 1);
}

/** The breed to draw: the tiered one, or the plain animal when there is no tier. */
export function breedLook(kind: AnimalKind, breed?: number): Breed {
  return breed === undefined
    ? PLAIN_ANIMAL[kind]
    : (BREEDS[kind][Math.min(BREEDS[kind].length - 1, Math.max(0, breed))] ?? PLAIN_ANIMAL[kind]);
}

/* ---------- buildings (D199, D268) ---------- */

/** D268: a plot's building, by the data's building tier ids. */
export type BuildingStyle = 'lean_to' | 'timber' | 'stone';

/** D268: a plot's building from the milestones reached: lean-to, then timber, then stone. */
export function styleOf(tier: number): BuildingStyle {
  let out: BuildingStyle = 'lean_to';
  for (const t of [...getIdleContent().collection.tiers].sort((a, b) => a.tier - b.tier))
    if (t.tier <= tier && t.id in STYLE_WALL) out = t.id as BuildingStyle;
  return out;
}

/** D268: the wall a plot's building has at each step. */
export const STYLE_WALL: Record<BuildingStyle, string> = {
  lean_to: '#9C8B6E',
  timber: '#8A5A34',
  stone: '#B9B2A2',
};

/**
 * D343: a chain building's own roof, by its id, standing in for the `use` category's shared
 * colour so sixteen processing sheds never all read as one grey row: each takes a tint from
 * what it makes (the maltings' malt gold, the winery's wine, the smokehouse's char), true to
 * type at a glance. Checked before `use` in both renderers; a building with no entry here
 * keeps the category's own roof.
 */
export const CHAIN_ROOF: Record<string, string> = {
  maltings: '#C9A227',
  distillery: '#8C5A2B',
  crisp_works: '#B98A4A',
  veg_box_shed: '#6F8F4A',
  cress_packhouse: '#3F8F6E',
  feed_store: '#C8B45A',
  egg_packing_shed: '#E4D9B8',
  smokehouse: '#4A3B33',
  trout_smokery: '#5C7A82',
  jam_kitchen: '#8C2F3A',
  honey_house: '#D99A2B',
  packhouse: '#B23B2E',
  bakery: '#A9702E',
  winery: '#5B3358',
  creamery: '#E8E2D0',
  drying_shed: '#D98A2B',
};

/**
 * How tall each building stands, in the flat map's screen units (a tile is 46 wide): the wall
 * and the roof's rise above it. The 3D world scales the same numbers, so a stone shed is as
 * much taller than a lean-to in both.
 */
export const BUILDING_HEIGHT = {
  barn: { wall: 30, rise: 21 },
  house: { wall: 21, rise: 17 },
  shed: { wall: 22, rise: 14 },
  lean_to: { wall: 15, rise: 8 },
  timber: { wall: 22, rise: 14 },
  stone: { wall: 25, rise: 14 },
  /** A silo's height, and its radius as a share of a tile's width per tile it is wide. */
  silo: { tall: 52, short: 52 * 0.8, radius: 0.3 },
} as const;

/** The farmhouse's chimney: a stack of brick, the barn's red a shade darker. */
export const CHIMNEY_BRICK_SHADE = 0.08;

/** Chimney smoke: the colour of a puff, three to a chimney, a little bigger each. */
export const SMOKE = { colour: '#EFEAE1', puffs: 3, r: 3.4, grow: 0.4, opacity: 0.42 } as const;

/* ---------- crops (D268, D269) ---------- */

/** Which plant shape a crop draws. Grain has a head, roots a leafy tuft, brassicas a ball. */
export const PLANT: Record<string, string> = {
  wheat: 'grain',
  barley: 'grain',
  oats: 'grain',
  rye: 'grain',
  maize: 'tall',
  carrots: 'carrot',
  potatoes: 'potato',
  sugar_beet: 'tuft',
  beet: 'tuft',
  onions: 'tuft',
  cabbage: 'ball',
  lettuce: 'ball',
  oilseed: 'flower',
  oilseed_rape: 'flower',
  // D300: the five added that generation.
  rapeseed: 'flower',
  hay: 'tall',
  strawberries: 'berry',
  watercress: 'tuft',
};

/** D269: the leaf colour of a crop grown for its root, drawn over its soil, never a ladder. */
export const ROOT_LEAF: Record<string, string> = {
  carrots: '#3E7D3A',
  potatoes: '#6E9A4C',
};

/** Each crop's own hue, folded into the field's ripening ladder so no two crops read alike. */
export const CROP_HUE: Record<string, Mix> = {
  wheat: ['#D9B24A', 0.2],
  barley: ['#C9C06A', 0.22],
  oats: ['#CFC98A', 0.2],
  carrots: ['#2F6B33', 0.2],
  potatoes: ['#4C7A63', 0.22],
  cabbage: ['#6FA08C', 0.3],
  beet: ['#8A5A4A', 0.18],
  maize: ['#8FA82E', 0.22],
  oilseed: ['#E0C22A', 0.34],
  // D300: the five added that generation, each its own hue so none reads as another's crop.
  rapeseed: ['#E0C22A', 0.34],
  hay: ['#C9BE7A', 0.26],
  watercress: ['#2E6B4A', 0.24],
};

/** The colours a crop shows at its stages, over its leaf. */
export const CROP_PARTS = {
  /** A ripe grain head, stage 5. */
  ripeGrain: '#E3A82B',
  /** Oilseed in flower, from stage 3. */
  rapeFlower: '#E8C81E',
  /** A carrot's orange shoulder, from stage 4. */
  carrot: '#E07A1F',
  /** Potato flowers, white and lilac, from stage 3. */
  potatoFlower: ['#F4F0F6', '#C9A9DA'],
  /** A potato showing at the foot of the plant when ripe, and seen from above. */
  tuber: '#C9A66B',
  tuberPlan: '#EDE4F2',
  /** D269: chaff off a busy plot. */
  chaff: '#EAD58A',
  /** D300: strawberries, low ground bushes: a white flower, then red berries. */
  berryFlower: '#F4F0EC',
  berry: '#C23A3E',
} as const;

/**
 * D300: tomatoes, grown under glass. A plot of glasshouses, one lean-to frame per few owned,
 * each a bit bigger with every milestone (D268), with vines of red fruit showing through the
 * glass once ripe.
 */
export const GLASSHOUSE = {
  frame: '#8C6A44',
  glass: '#CFE0D8',
  glassAlpha: 0.4,
  leaf: '#3E7A34',
  tomato: '#C23A2E',
  /** How tall a glasshouse stands at tier 0, and how much taller with every milestone (D268). */
  wall: 10,
  rise: 7,
  growPerTier: 0.6,
} as const;

/** D268: how much closer the rows are planted per milestone, up to four of them. */
export function cropDensity(tier = 0): number {
  return 1 + Math.min(4, tier) * 0.12;
}

/** The spacing of plants down a row and between rows, in tiles, at density 1. */
export const PLANT_STEP = { x: 0.32, y: 0.38 } as const;

/* ---------- grass, flowers, bales ---------- */

/** Grass tufts and meadow flowers: how many to a tile, and the flowers' colours. */
export const MEADOW = {
  tuftsPerTile: 7,
  flowersPerTile: 3,
  flowers: ['#E3C64A', '#D98E9E', '#EDE4C8', '#C9A2CE'],
  frostFlower: '#D8DCD2',
  /** A tuft is the ground's own colour, darker. */
  tuftShade: 0.42,
} as const;

/** A round hay bale: its barrel, its end and the twine. Three to a grass meadow. */
export const BALE = { body: '#D9BE6B', end: '#C4A44F', core: '#A98B3C', perMeadow: 3 } as const;

/* ---------- hedges ---------- */

/**
 * The hedge round the world and the lower bank between two fields (D161): a box on its line,
 * its top lit towards a pale green. Heights in the flat map's screen units, widths in tiles.
 */
export const HEDGE = {
  topMix: ['#C8D9A8', 0.34] as Mix,
  leftShade: 0.34,
  rightShade: 0.16,
  world: { ht: 13, w: 0.14 },
  bank: { ht: 7, w: 0.1 },
} as const;

/* ---------- trees and water (D267) ---------- */

export const TREE = {
  /** A wood tree's size; an orchard's grows with the milestones (D268). */
  woodSize: 12,
  orchardSize: (tier = 0) => 10 + Math.min(4, tier) * 1.2,
  /** An orchard's leaf, the palette's orchard brightened. */
  orchardLeaf: ['#8FBF52', 0.3] as Mix,
  blossom: '#F2D9E2',
  fruit: '#C0472E',
  frost: '#E6E8E4',
  /** Trunks are the soil, darker. */
  trunkShade: 0.34,
  /** The seeded trees round the world are the wood's colour hazed back, a little lighter. */
  country: { meadowMix: 0.2, dark: 0.44, mid: 0.3, lit: 0.15 },
} as const;

/** D267: the stream, drawn in the map's own hand: a bank, the water, a lighter ripple. */
export const STREAM = {
  /** The token's water brought towards the map's own brighter water, so it reads as water. */
  waterToBase: 0.5,
  /** The bank is earth: the hazed pasture, darker, towards the soil. */
  bankShade: 0.18,
  bankToSoil: 0.45,
  rippleTint: 0.35,
} as const;

/* ---------- small things on a plot (D251, D268) ---------- */

export const HIVE = {
  body: '#F2ECDC',
  band: '#C9BE9E',
  door: '#4A3F2A',
  lid: '#8C7B5A',
  /** From above: the lid, pale, with a line round it. */
  planLid: '#E9E2CF',
  planEdge: '#9C8F6E',
} as const;
export const VINE = { stake: '#6B5434', leaf: '#4E7A34', grapes: '#5B3A6E' } as const;
export const HOP = { pole: '#7A5A38', wire: '#6B5434', bine: '#3F6B2C', cone: '#B8D27A' } as const;
export const CROCUS = { stem: '#5E8A3A', cup: '#8E5BB5', stigma: '#E07A1F' } as const;
export const PUDDLE = '#5C4128';

/** D268: a hand at work on a plot, in a smock and a straw hat with a hoe. */
export const WORKER = {
  legs: '#4A3F2A',
  smock: '#5B7FA6',
  skin: '#E8C3A0',
  hat: '#D9B85C',
  handle: '#7A5A38',
  blade: '#6B6B66',
} as const;

/**
 * D353: a worked field's own cycle, on the render clock and never the store's, so a tap or a
 * rebuild cannot step it. The whole field passes through its stages together: growing up to
 * `grow` of the way round, ripe to `ripe`, harvested by its workers (chaff over it) to
 * `harvest`, then cut bare until it regrows. `ms` is one cycle; each field starts at its own
 * point in it, so neighbouring fields do not change together. Under reduced motion every
 * field holds still at `still`. Looks only: the sim is untouched.
 */
export const FIELD_CYCLE = {
  ms: 45_000,
  grow: 0.5,
  ripe: 0.7,
  harvest: 0.85,
  still: 0.6,
} as const;

/** The 3D map's slow orbit round the world when asked for (the landing screen's world):
 *  OrbitControls' `autoRotateSpeed`, where 2 is one turn a minute at 60 frames a second. */
export const CAMERA_ORBIT = { speed: 0.5 } as const;

/** D268: the tier sign: a post and a board with the last milestone reached. */
export const TIER_SIGN = { post: '#7A5A38', board: '#F2EDE1', ink: '#3A3428' } as const;

/** D268: a milestone's moment on its plot. */
export const BURST = '#F2C94C';

/** D345: a crop's "ready" badge in both renderers: its rim, its face, the icon's ink, and the
 *  icon shown when the row has none of its own. */
export const READY_BADGE = { rim: BURST, face: '#ffffff', ink: '#4A3B2A', fallback: 'sprout' };

/* ---------- festival marks (D245) ---------- */

/** The materials the festival marks are made of. */
export const MARK = {
  stone: '#B9B2A2',
  stoneDark: '#8C8576',
  wood: '#7A5A38',
  leaf: '#4E7A34',
  blossom: '#F2ECDC',
  white: '#E8E1CF',
  dovecote: '#EFE8D6',
  tile: '#9C5B3E',
  hole: '#4A3F2A',
  flame: '#E8862A',
  ribbons: ['#C8473A', '#3E7CB1', '#E0B83A'],
} as const;

/** D259: how much bigger than their first drawing animals and festival marks are drawn. */
export const ANIMAL_SCALE = 2,
  MARK_SCALE = 2.2;

/* ---------- the silo and the barn's roof ---------- */

/**
 * The silo's wall: the palette's stone lifted towards white, which on the flat map reads as a
 * white tower. The 3D world's sun falls on a round wall at a slant, so it lifts it further to
 * read as the same white.
 */
export const SILO = { tint: 0.22, tint3d: 0.62, band: 0.4 } as const;

/** The barn's gambrel: its lower slope a shade down, its upper slope lifted, from its roof. */
export const BARN_ROOF = { lower: 0.06, upper: 0.14, upper3d: 0.3 } as const;

/* ---------- decorations (D291, D331) ---------- */

/** What a building decoration does to every building's walls and roof. Classic is none. */
export const BUILDING_DECOR: Record<string, { wall?: string; barn?: string; roof?: string }> = {
  building_whitewash: { wall: '#F1EEE6', barn: '#EDE9DF' },
  building_pink_limewash: { wall: '#E8C4B8', barn: '#E2BAAD' },
  building_brick: { wall: '#A2533B', barn: '#9A4A34' },
  building_weatherboard: { wall: '#3A3632', barn: '#34302C' },
  building_thatch: { roof: '#C9A45C' },
  building_flint: { wall: '#8E8A84', barn: '#86817A' },
  building_honey_stone: { wall: '#D6B57A', barn: '#CDAA6E' },
};

/** D331: what a roof decoration lays on every roof. As built is none. */
export const ROOF_DECOR: Record<string, string> = {
  roof_slate: '#4F5560',
  roof_pantiles: '#C0683E',
  roof_mossy: '#6E7D4C',
  roof_copper: '#5FA08A',
};

/**
 * D331: a building's look is its building decoration's id, and its roof decoration's after a
 * `+` when one is chosen. Both renderers read the walls and roof through this one reading.
 */
export function buildingDecor(look?: string): { wall?: string; barn?: string; roof?: string } {
  if (!look) return {};
  const [walls, roof] = look.split('+');
  const out = { ...(BUILDING_DECOR[walls ?? ''] ?? {}) };
  const r = roof ? ROOF_DECOR[roof] : undefined;
  if (r) out.roof = r;
  return out;
}

/** What a path decoration lays the yard with. Plain is the yard's own ground. */
export const PATH_DECOR: Record<string, string> = {
  path_cobbles: '#A39D92',
  path_gravel: '#C4B9A4',
  path_brick: '#A8604A',
  path_flagstones: '#CFC8B6',
  path_york_stone: '#B7A88E',
  path_autumn_leaves: '#B8742E',
};

/** The fence a pasture has: post and rail, picket, woven hurdle or dry stone. */
export type FenceStyle = 'fence_rail' | 'fence_picket' | 'fence_hurdle' | 'fence_dry_stone';
export const FENCE_DECOR: Record<string, string> = {
  fence_picket: '#EEEBE2',
  fence_white_rail: '#ECE8DE',
  fence_hurdle: '#8C6A3E',
  fence_green_picket: '#4E7356',
  fence_dry_stone: '#A8A196',
  fence_slate_wall: '#5E626A',
};

/** D331: the shape each decorated fence is drawn in; a colour of its own comes from FENCE_DECOR. */
export const FENCE_SHAPE: Record<string, FenceStyle> = {
  fence_picket: 'fence_picket',
  fence_white_rail: 'fence_rail',
  fence_hurdle: 'fence_hurdle',
  fence_green_picket: 'fence_picket',
  fence_dry_stone: 'fence_dry_stone',
  fence_slate_wall: 'fence_dry_stone',
};

/** How tall each decorated fence shape stands, in the flat map's pixels; post and rail is 11. */
export const FENCE_HEIGHT: Record<string, number> = {
  fence_picket: 10,
  fence_hurdle: 9,
  fence_dry_stone: 8,
};

/** The tractor's paint, by vehicle decoration. */
export const VEHICLE_DECOR: Record<string, string> = {
  vehicle_red: '#B8352B',
  vehicle_green: '#3E7A46',
  vehicle_blue: '#2E5F9E',
  vehicle_yellow: '#E0B83A',
  vehicle_orange: '#D9752B',
  vehicle_vintage_grey: '#8D9396',
  vehicle_show_maroon: '#6E2433',
};

/** D331: the world's own hedge, by hedge decoration. Hawthorn is the palette's own hedge. */
export const HEDGE_DECOR: Record<string, string> = {
  hedge_beech: '#7E8F3A',
  hedge_holly: '#2F5530',
  hedge_copper_beech: '#7A3F33',
  hedge_yew: '#24402C',
  hedge_may_blossom: '#C9D3B2',
};

/** D331: the world gate's and every plot gate's paint. Oak is the timber's own colour. */
export const GATE_DECOR: Record<string, string> = {
  gate_white: '#EEEBE2',
  gate_green: '#3F6B4A',
  gate_iron: '#2B2B2E',
  gate_primrose: '#E6D27A',
};

/**
 * D331: what the hands wear, by worker decoration: smock (or coat), hat and legs. The blue smock
 * and straw hat are WORKER's own.
 */
export const WORKER_DECOR: Record<string, { smock: string; hat: string; legs: string }> = {
  worker_red_check: { smock: '#B8443A', hat: '#D9B85C', legs: '#3E4A5A' },
  worker_green_overalls: { smock: '#4E7356', hat: '#E8E1CF', legs: '#4E7356' },
  worker_tweed: { smock: '#7C6A4E', hat: '#5A4E3E', legs: '#4A3F2A' },
  worker_waxed_jacket: { smock: '#4A5240', hat: '#3A3028', legs: '#2E3640' },
  worker_show_best: { smock: '#F1EEE6', hat: '#C8473A', legs: '#2E3640' },
  worker_harvest_gold: { smock: '#E0B83A', hat: '#B8742E', legs: '#5A4632' },
};

/** D331: the hands' clothes for a worker decoration, or the plain ones. */
export function workerLook(id?: string): { smock: string; hat: string; legs: string } {
  return (id && WORKER_DECOR[id]) || WORKER;
}

/* ---------- the track and the river (D287) ---------- */

/** The track's surface, by the data's surface id. */
export const TRACK_SURFACE: Record<string, string> = {
  dirt: '#9C7B52',
  gravel: '#B9B0A0',
  stone: '#A9A49A',
};

/** How wide the track is, in tiles, and the bridge's timber. */
export const TRACK = { width: 0.32, bridge: '#8C6A44', rail: '#6B5434' } as const;

/** The river's widths in the flat map's pixels, as the flat map strokes it. */
export const RIVER = { bank: 19, water: 15, ripple: 6 } as const;

/* ---------- day and night (D288) ---------- */

/** The palette's time of day for each of the data's day phases. */
export const PHASE_TIME: Record<string, string> = {
  dawn: 'dawn',
  day: 'morning',
  dusk: 'dusk',
  night: 'night',
};

/**
 * The 3D world's light in each phase: the sun, the sky's fill, and the dark it is hazed into.
 * D318: the sun carries more of the light and the sky's fill less than D288 shipped, so a
 * shadow and a face turned from the sun read as darker; `rim` is the edge light (its colour and
 * strength) that lifts the rim of a rounded thing away from what is behind it, strongest at night.
 *
 * D381: the world is tone mapped, so these are light, not paint: a warm sun lower in the sky
 * (`sunH`, its height as a share of the world's reach, so a lower sun casts longer shadows), a
 * blue fill from the sky (`sky`) over a warm bounce from the ground (`ground`), and `exposure`,
 * how bright the whole picture is drawn. The sky behind the world runs from the haze at the
 * foot of the screen up towards `zenith`, mixed in by `zenithT`. `fine` is how much of good
 * weather's warmth (D286, `GOOD_WEATHER`) the hour takes: all of it by day, half at dawn and
 * dusk, none at night, so good weather never flattens the dark.
 */
export const PHASE_LIGHT: Record<
  string,
  {
    fine: number;
    sun: string;
    sunI: number;
    sunH: number;
    sky: string;
    ground: string;
    skyI: number;
    haze: string;
    hazeT: number;
    zenith: string;
    zenithT: number;
    rim: string;
    rimI: number;
    exposure: number;
  }
> = {
  dawn: {
    fine: 0.5,
    sun: '#FFC496',
    sunI: 2.3,
    sunH: 0.4,
    sky: '#B8BEE6',
    ground: '#8A6A54',
    skyI: 1.2,
    haze: '#F2BE9A',
    hazeT: 0.28,
    zenith: '#A9B6E2',
    zenithT: 0.45,
    rim: '#FFDDBC',
    rimI: 0.22,
    exposure: 1,
  },
  /* D381: a warm sun a little lower than D318's, with a cooler, weaker sky fill, so a sunlit
     face and a shaded one read clearly apart and the shade is faintly blue. */
  day: {
    fine: 1,
    sun: '#FFD9A2',
    sunI: 3.5,
    sunH: 0.46,
    sky: '#98B8EE',
    ground: '#9A7A50',
    skyI: 0.95,
    haze: '#F6F0E2',
    hazeT: 0.12,
    zenith: '#9FC0EA',
    zenithT: 0.5,
    rim: '#FFF6E6',
    rimI: 0.15,
    exposure: 1.05,
  },
  /* D381: a gold evening, the sun low and amber and the shade a soft violet. */
  dusk: {
    fine: 0.5,
    sun: '#FFB062',
    sunI: 2.8,
    sunH: 0.34,
    sky: '#A6A4D4',
    ground: '#8A5A44',
    skyI: 1.3,
    haze: '#EFA878',
    hazeT: 0.4,
    zenith: '#7C82C4',
    zenithT: 0.55,
    rim: '#FFCB94',
    rimI: 0.3,
    exposure: 1.1,
  },
  /* D305: darker and bluer than day, but the moon and the sky's fill are both raised from
     what D288 shipped, so a tree, a crop and an animal stay apart by eye rather than all
     reading as one dark mass. D381: a deep blue night under a cool moon, drawn darker, so
     lit windows and rack lights (unlit, and never tone mapped) stand out against it. */
  night: {
    fine: 0,
    sun: '#A8B8EA',
    sunI: 1.15,
    sunH: 0.9,
    sky: '#5A67A0',
    ground: '#2E3452',
    skyI: 1.3,
    haze: '#151B36',
    hazeT: 0.66,
    zenith: '#0B1030',
    zenithT: 0.5,
    rim: '#9EB8FF',
    rimI: 0.36,
    exposure: 0.88,
  },
};

/**
 * D318: what gives the 3D world its depth, and what it may cost.
 *
 * - `shadowOffMs`: once the middle of the last `shadowWindow` frames' draw times passes this,
 *   the sun stops casting shadows for the rest of the visit (D286's budget); the contact shade
 *   under each thing stays, so the world keeps its feet on the ground.
 * - `contact`: the soft dark under a tree, a building, an animal or a person, as a share of
 *   black, and how far past the thing's own footprint it spreads.
 * - `country`: how far the country's grass is hazed back from the world's own meadow green.
 * - `hills`: the country's rise and fall beyond the fences: flat for `flat` tiles round the plot
 *   and the river so nothing standing there floats, then up to `height` over `ramp` tiles, in
 *   swells `scale` tiles across; `light` and `dark` tint its tops and hollows.
 * - `patches`: lighter and darker patches on the ground inside the fences, per tile, how big,
 *   and how far each is shaded from the ground's own colour.
 * - `kerb`: the low raised edge round a field or a grass plot, its width, height and shade, and
 *   `ridge`, the height of a furrow's ridge once a field is sown.
 * - `hedgeClumps`: the rounded clumps along a hedge's top, how far apart, as a share of its width.
 */
export const DEPTH = {
  shadowOffMs: 9,
  shadowWindow: 60,
  contact: { opacity: 0.42, spread: 1.5 },
  country: { haze: 0.3 },
  hills: { flat: 2.5, river: 1.4, ramp: 6, height: 1.1, scale: 5, light: 0.12, dark: 0.2 },
  patches: { perTile: 1.6, size: [0.35, 0.8] as const, shade: 0.1, tint: 0.08 },
  kerb: { w: 0.06, h: 0.035, shade: 0.22 },
  ridge: 0.028,
  hedgeClumps: { step: 0.24, size: 1.35, jitter: 0.25 },
} as const;

/**
 * D286: good weather shows as warmer, brighter sunlight over the whole world, never as shapes
 * drawn on it: the sun's colour and the sky's are mixed towards these, and the sun brightened.
 */
export const GOOD_WEATHER = { sun: '#FFD88A', sky: '#FFF0D0', mix: 0.45, boost: 1.2 } as const;

/**
 * D381: how much richer the map's greens (grass, woods, hedges, growing crops) and its roofs
 * are drawn than the page's own tokens, in both renderers alike: a model village, not a poster.
 */
export const RICHER = { greens: 0.16, roofs: 0.14 } as const;

/** A window lit at dusk and through the night. */
export const LIT_WINDOW = '#FFD27A';

/** Snow on the ground and roofs in winter, and how far it whitens them. */
export const SNOW = { colour: '#F4F6F4', ground: 0.35, roof: 0.6 } as const;

/* ---------- life on the world (D286, D289) ---------- */

export const WILDLIFE = {
  rabbit: '#8A7258',
  hare: '#A0805A',
  pheasant: '#9A4E2A',
  pheasantHead: '#2E5A3A',
  fox: '#C4632A',
  bird: '#3A3A40',
  bee: '#E0B83A',
  butterfly: ['#F2D24A', '#F4F1E8', '#E8A0B8'],
} as const;

/** Who calls at the gate (D289), by the data's visitor id. */
export const VISITOR: Record<string, { body: string; coat: string }> = {
  vet: { body: '#E9E6DE', coat: '#3E7A6A' },
  delivery_van: { body: '#E9E6DE', coat: '#B8352B' },
  villager: { body: '#E9E6DE', coat: '#8A5A9A' },
};

/** A lucky find (D244): a sack of coins glinting in the grass. */
export const FIND = { sack: '#C9A66B', coin: '#F2C94C' } as const;

/** D301: a trout, just under the surface of its pond: an olive back, a pale belly. */
export const FISH = { body: '#6E7A52', belly: '#D9CFA8' } as const;

/* ---------- D329: the country beyond the fence, and a fuller world ---------- */

/**
 * D329: the neighbours' land round the world, an English patchwork laid from the world's seed
 * (`landscapeFor` in `idle/map.ts`). Sizes are in tiles. The fields sit on a fixed grid, so the
 * world growing only swallows the nearest of them; the rest never move.
 */
export const LANDSCAPE = {
  /** How far past the land in use the fields run in the 3D world, and on the flat map. */
  reach: 26,
  flatReach: 18,
  /** A field's width and depth before it is cut back from the world, the river or the lane. */
  field: { w: [4, 8], h: [3, 7] } as const,
  /** The grass left between the world's own hedge and a neighbour's field. */
  gap: 1,
  /** The smallest piece of a cut-back field that is still drawn. */
  minSide: 2,
  minArea: 6,
  /** How often a field is a copse, and how often grazing, rather than a crop. */
  copse: 0.1,
  pasture: 0.3,
  /** A hedge run longer than four tiles has a gateway this often, and a tree per tile this often. */
  gateway: 0.45,
  hedgeTree: 0.2,
  /** Trees to a tile of copse. */
  copseTrees: 0.45,
  /** The lane from the world gate: half its width, and its hedges either side. */
  lane: { half: 0.35, hedge: 0.8 },
  /** The village near the far edge: how far out from the land in use, and how many houses. */
  village: { dist: 17, houses: 7 },
  /** The 3D haze: none this close to the land in use, all of it this far out. */
  haze: { near: 4, far: 30 },
  /** The crops the neighbours grow, in the order a seed picks from. */
  crops: ['wheat', 'barley', 'rapeseed', 'beans', 'oats', 'plough', 'hay'] as const,
  /** Grazing has a few beasts on it, to a tile. */
  grazing: 0.08,
} as const;

/**
 * D329: a neighbour's field through the year, and the pattern drawn on it: `rows` of a sown
 * crop, `speckle` of flowers, `furrow` of ploughland, `stripes` of a mown hay field, `mottle`
 * of grazing, `wood` for a copse's floor.
 */
export const LANDSCAPE_CROP: Record<
  string,
  { spring: string; summer: string; autumn: string; winter: string; look: string }
> = {
  wheat: {
    spring: '#7FA650',
    summer: '#D9B85A',
    autumn: '#CDBD8C',
    winter: '#8FA062',
    look: 'rows',
  },
  barley: {
    spring: '#8DB05E',
    summer: '#E3D08E',
    autumn: '#BBA06E',
    winter: '#94805F',
    look: 'rows',
  },
  rapeseed: {
    spring: '#D8C94E',
    summer: '#7E9A4A',
    autumn: '#9A7E5C',
    winter: '#7C9558',
    look: 'speckle',
  },
  beans: {
    spring: '#6E9446',
    summer: '#5F7F3C',
    autumn: '#6E5C40',
    winter: '#8B7355',
    look: 'rows',
  },
  oats: {
    spring: '#88AE5E',
    summer: '#C9C27E',
    autumn: '#BFAF84',
    winter: '#8B7355',
    look: 'rows',
  },
  plough: {
    spring: '#7F8C52',
    summer: '#8E6E4E',
    autumn: '#8A6A4A',
    winter: '#7E624A',
    look: 'furrow',
  },
  hay: {
    spring: '#86B25A',
    summer: '#B9C46E',
    autumn: '#88A85A',
    winter: '#7E9A5C',
    look: 'stripes',
  },
  pasture: {
    spring: '#7FAE58',
    summer: '#7AA556',
    autumn: '#7EA05A',
    winter: '#7A9660',
    look: 'mottle',
  },
  copse: {
    spring: '#4F6F3A',
    summer: '#4A6A36',
    autumn: '#5E5A34',
    winter: '#5A5A44',
    look: 'wood',
  },
};

/** D329: the village: walls and roofs a seed picks from, and the church's stone. */
export const VILLAGE = {
  walls: ['#E6DCC6', '#D8C7A4', '#C9A27E', '#EDE6D6'],
  roofs: ['#8C4A36', '#6E5A4E', '#9A5B3E', '#5E5048'],
  stone: '#B8AE98',
  spire: '#6E6A62',
} as const;

/**
 * D329: the things that fill the world in: the headland round a field, a gateway into a plot,
 * a trough in a pen, bales stacked in the yard, reeds on the river bank, a cart and the tools
 * left out, and more beasts and hands as the milestones are reached.
 */
export const DENSITY = {
  headland: { w: 0.14, tint: 0.14 },
  trough: { body: '#7E8488', water: '#6E9CB8' },
  cart: { body: '#8C6A44', wheel: '#4A3826', load: '#D9BE6B' },
  tool: { handle: '#7A5A38', metal: '#9AA0A4' },
  churn: '#C9CDD0',
  gate: '#8C6A44',
  path: { shade: 0.16 },
  reed: { stem: '#6E8A46', head: '#7A5A38', perTile: 1.6 },
  /** A beast more in a pen for every milestone, up to this many more. */
  animalsPerTier: 1,
  maxExtraAnimals: 6,
  /** A hand more on a plot for every this many milestones, up to this many in all. */
  tiersPerWorker: 2,
  maxWorkers: 3,
} as const;

/**
 * D381: the country's trees by kind. Each kind's greens are the season's wood mixed towards
 * its own leaf, so all three still turn with the year; the conifer keeps most of its dark green.
 * `share` is how often a copse or wood tree is each kind (the rest are oaks); a hedgerow tree is
 * an oak or now and then a birch, never a conifer.
 */
export const COUNTRY_TREE = {
  oak: { leaf: '#4F8236', mix: 0.45, trunk: '#5A4632' },
  conifer: { leaf: '#2F5E44', mix: 0.75, trunk: '#4A3A2C' },
  birch: { leaf: '#A6C45C', mix: 0.6, trunk: '#E2DED2', bark: '#4A4440' },
  share: { conifer: 0.24, birch: 0.2 },
  hedgeBirch: 0.25,
  /** The leaf hazed this much towards the distance, the 3D haze doing the rest. */
  haze: 0.35,
  /** A crown's back, middle and lit top, and how far one tree's green strays from the next. */
  tone: { dark: 0.24, mid: 0.08, lit: 0.08, jitter: 0.07 },
} as const;

export type CountryTreeKind = 'oak' | 'conifer' | 'birch';

/** D381: which kind a country tree is, from where it stands; the same in both renderers. */
export function countryTreeKind(x: number, y: number, hedge = false): CountryTreeKind {
  let h =
    (Math.imul(Math.round(x * 100), 0x27d4eb2d) ^ Math.imul(Math.round(y * 100), 0x165667b1)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
  const u = ((h ^ (h >>> 13)) >>> 0) / 4294967296;
  if (hedge) return u < COUNTRY_TREE.hedgeBirch ? 'birch' : 'oak';
  const s = COUNTRY_TREE.share;
  return u < s.conifer ? 'conifer' : u < s.conifer + s.birch ? 'birch' : 'oak';
}

/**
 * D381: the neighbours' land up close: the ridges of a sown or ploughed field drawn standing
 * within `rowsNear` tiles of the world, a field gate and the track through it, the lane's grass
 * verge, the telegraph poles along it, and the farmsteads out across the fields.
 */
export const COUNTRY_DETAIL = {
  rowsNear: 11,
  row: { step: 0.34, w: 0.13, h: 0.045 },
  furrow: { step: 0.26, w: 0.14, h: 0.05 },
  /** Small square bales left in rows on a mown hay field in summer and autumn. */
  hayBale: '#D8C27A',
  gate: '#9A8466',
  track: { colour: '#A08A68', reach: 1.6, w: 0.32 },
  verge: { w: 0.3, tint: 0.1, flowers: '#F1EEDF' },
  pole: { colour: '#6A5642', every: 5 },
  /** How many farmsteads stand out across the fields, and how far from the land in use. */
  farmsteads: 2,
  farmstead: { near: 9, far: 20 },
  farmhouse: { wall: '#E4D8BE', roof: '#7A4A38' },
  farmbarn: { wall: '#8C3B2E', roof: '#5E5650' },
  /** A small clump of trees in a field's corner, this often, of two to five trees. */
  clump: 0.22,
} as const;

/* ---------- D381: the world's most-seen objects ---------- */

/** D381: the barn's and sheds' trim and weathering, and the pens' rails. */
export const BUILDING_DETAIL = {
  /** The dirt washed up the foot of a wall: this much of the soil, this high. */
  weather: { soil: 0.28, h: 0.09 },
  /** Boards up a barn's walls, so it reads as timber. */
  boards: 10,
  boardShade: 0.14,
  /** The barge boards and ridge in the trim colour; a hay loft door in the gable. */
  barge: 0.035,
  ridgeShade: 0.22,
  loftShade: 0.42,
  window: '#5E7A86',
} as const;

/** D381: the parts that make each beast read at a glance. */
export const BEAST_PARTS = {
  udder: '#E7B7A8',
  muzzle: '#D9B9A4',
  horn: '#E8E0CC',
  snout: '#E39C98',
  wattle: '#C8352A',
} as const;

/** D329: the neighbours' beasts, small and far off: a body and a head. */
export const GRAZING = {
  sheep: { body: '#EEE9DC', head: '#3A3430' },
  cow: { body: '#6B4A36', head: '#3A2A20' },
} as const;

/* ---------- D327: the monument ground ---------- */

/**
 * D327: once every kind of festival mark stands on the world, each later festival adds a
 * memorial to a monument ground instead of another mark in the rows: up to `perGround` on a
 * ground, the grounds taking turns as a stone circle, a chapel yard and a memorial garden.
 * The memorials vary in material, size and form.
 */
export const MEMORIAL = {
  perGround: 6,
  styles: ['circle', 'chapel', 'garden'] as const,
  materials: {
    stone: { face: '#B9B2A2', dark: '#8C8576' },
    brick: { face: '#A9583E', dark: '#7A3C2A' },
    timber: { face: '#8A6A44', dark: '#5E4630' },
    flint: { face: '#8F918C', dark: '#5A5C58' },
  } as Record<string, { face: string; dark: string }>,
  path: '#D6CBB0',
  wall: '#A8A08E',
  shrub: '#4E7A34',
  chapel: { wall: '#E6DCC6', roof: '#6E5A4E' },
} as const;

export type MemorialForm = 'stone' | 'cross' | 'pillar' | 'cairn' | 'post' | 'chapel' | 'shrub';

/** D327: one thing standing on a monument ground, in tiles; `w` and `h` for the chapel. */
export interface MemorialPiece {
  x: number;
  y: number;
  form: MemorialForm;
  material: string;
  size: number;
  w?: number;
  h?: number;
}

/** D327: what a monument ground holds: its pieces, its paths (`[x1, y1, x2, y2]`), a low wall. */
export interface MonumentGround {
  pieces: MemorialPiece[];
  paths: Array<[number, number, number, number]>;
  wall: boolean;
}

const MATERIAL_ORDER = ['stone', 'brick', 'flint', 'timber'] as const;
const FORM_OF: Record<string, MemorialForm[]> = {
  stone: ['stone', 'cross'],
  brick: ['pillar'],
  flint: ['cairn'],
  timber: ['post', 'cross'],
};

/**
 * D327: a monument ground laid out on `b`, for `count` memorials, the `n`th ground on the
 * world. Each memorial keeps its place as the next is added, so a festival never moves the
 * ones before it. Pure: both renderers draw from it.
 */
export function monumentGround(
  b: { x: number; y: number; w: number; h: number },
  n: number,
  count: number,
): MonumentGround {
  const style = MEMORIAL.styles[n % MEMORIAL.styles.length];
  const cx = b.x + b.w / 2,
    cy = b.y + b.h / 2;
  const piece = (i: number, x: number, y: number, only?: string): MemorialPiece => {
    const material = only ?? (MATERIAL_ORDER[(i * 3 + n) % MATERIAL_ORDER.length] as string);
    const forms = FORM_OF[material] ?? ['stone'];
    return {
      x,
      y,
      material,
      form: forms[(i + n) % forms.length] as MemorialForm,
      size: 0.7 + (((i * 37 + n * 11) % 5) / 4) * 0.4,
    };
  };
  const out: MonumentGround = { pieces: [], paths: [], wall: false };
  if (style === 'circle') {
    // Standing stones in a ring, stone and flint, round a worn ring path.
    const rx = b.w / 2 - 0.45,
      ry = b.h / 2 - 0.4;
    const N = MEMORIAL.perGround;
    for (let k = 0; k < N; k++) {
      const a0 = (k / N) * Math.PI * 2,
        a1 = ((k + 1) / N) * Math.PI * 2;
      out.paths.push([
        cx + Math.cos(a0) * rx * 0.75,
        cy + Math.sin(a0) * ry * 0.75,
        cx + Math.cos(a1) * rx * 0.75,
        cy + Math.sin(a1) * ry * 0.75,
      ]);
    }
    for (let i = 0; i < count; i++) {
      const a = (i / N) * Math.PI * 2 + 0.3;
      const p = piece(
        i,
        cx + Math.cos(a) * rx,
        cy + Math.sin(a) * ry,
        i % 3 === 2 ? 'flint' : 'stone',
      );
      p.form = p.material === 'flint' ? 'cairn' : 'stone';
      out.pieces.push(p);
    }
    return out;
  }
  const wide = b.w >= b.h;
  if (style === 'chapel') {
    // A small chapel at one end, a path from its door, memorials in rows either side, a low wall.
    out.wall = true;
    const cw = wide ? Math.min(1.4, b.w * 0.35) : b.w - 0.6,
      ch = wide ? b.h - 0.7 : Math.min(1.4, b.h * 0.35);
    out.pieces.push({
      x: b.x + 0.25 + cw / 2,
      y: wide ? cy : b.y + 0.25 + ch / 2,
      form: 'chapel',
      material: 'stone',
      size: 1,
      w: cw,
      h: ch,
    });
    if (wide) out.paths.push([b.x + 0.25 + cw, cy, b.x + b.w - 0.1, cy]);
    else out.paths.push([cx, b.y + 0.25 + ch, cx, b.y + b.h - 0.1]);
    const free = wide ? b.w - cw - 0.5 : b.h - ch - 0.5;
    const cols = Math.ceil(MEMORIAL.perGround / 2);
    for (let i = 0; i < count; i++) {
      const col = Math.floor(i / 2),
        side = i % 2 === 0 ? -1 : 1;
      const t = 0.25 + cw + 0.25 + ((col + 0.5) / cols) * free;
      out.pieces.push(
        wide
          ? piece(i, b.x + t, cy + side * (b.h / 4 + 0.05))
          : piece(i, cx + side * (b.w / 4 + 0.05), b.y + t),
      );
    }
    return out;
  }
  // The memorial garden: paths in a cross, memorials in the beds, shrubs at the path ends.
  out.paths.push([b.x + 0.1, cy, b.x + b.w - 0.1, cy], [cx, b.y + 0.1, cx, b.y + b.h - 0.1]);
  for (const [x, y] of [
    [b.x + 0.3, cy - 0.3],
    [b.x + b.w - 0.3, cy + 0.3],
  ] as const)
    out.pieces.push({ x, y, form: 'shrub', material: 'stone', size: 1 });
  const beds: Array<[number, number]> = [];
  for (const fy of [0.25, 0.75])
    for (const fx of wide ? [0.15, 0.38, 0.62, 0.85] : [0.25, 0.75])
      beds.push([b.x + b.w * fx, b.y + b.h * fy]);
  const order = [1, 6, 2, 5, 0, 7, 3, 4].filter((k) => k < beds.length);
  for (let i = 0; i < count; i++) {
    const at = beds[order[i % order.length] as number] as [number, number];
    out.pieces.push(piece(i, at[0], at[1]));
  }
  return out;
}

/**
 * D422: the 3D asset module's colours (`assets/idle-worlds-assets.js`), moved here from the
 * module and passed back in as its `palette`, so no renderer hard-codes a colour. Keyed by the
 * module's own names: shared, then motorsport, arena, compute and farm.
 */
export const ASSET_PALETTE = {
  // shared
  tyre: '#26272a',
  window: '#2a2c30',
  lampWarm: '#ffd27a',
  metal: '#9aa0a6',
  white: '#f2f0ea',
  // motorsport
  tarmac: '#55585c',
  tarmacLight: '#6a6d72',
  kerbRed: '#c7372b',
  kerbWhite: '#f2f0ea',
  team: '#d9622b',
  teamDark: '#a8481e',
  rival: '#2e5f9e',
  rival2: '#3e7a46',
  shedWall: '#e8e4dc',
  shedRoof: '#8e9399',
  glass: '#a9c6d6',
  gravel: '#c9b994',
  mud: '#7c5a3a',
  screen: '#7fd0ff',
  charge: '#6fe39a',
  grass: '#8fb066',
  canvas: '#f4f1ea',
  // arena
  stone: '#a8a39a',
  stoneDark: '#8f8a82',
  stoneCap: '#bdb8ad',
  timber: '#b4924f',
  timberDark: '#7a5a2e',
  thatch: '#c9a45c',
  roofRed: '#7a3b2e',
  slate: '#4e6e8e',
  fire: '#e07a2c',
  ember: '#ffb347',
  crystal: '#3fb7a8',
  crystalGlow: '#9ce5dc',
  banner: '#b4432e',
  bannerBlue: '#2e5f9e',
  tent: '#d8cdb4',
  water: '#5f8fb0',
  courtGrass: '#9cb873',
  sand: '#d6c08e',
  horse: '#7a5233',
  dragon: '#9b2f26',
  leaf: '#4f7a3c',
  // compute accents
  tray: '#d9622b',
  trayBlue: '#2e5f9e',
  stack: '#e2b93b',
  greenRoof: '#6f9a5b',
  solar: '#2b3f5c',
  solarCell: '#38557d',
  plantWhite: '#f4f4f0',
  cool: '#7fb3c8',
  kerbYellow: '#e2b93b',
  verge: '#9ebc7a',
  // farm
  hedge: '#4f7a3c',
  hedgeLight: '#5f8f48',
  farmVerge: '#c7d39a',
  soil: '#8a6a4b',
  wheat: '#d9a63c',
  barley: '#e7cf86',
  potato: '#6f8f4a',
  carrotTop: '#5e9550',
  hay: '#d8c27a',
  rape: '#e8d23a',
  pen: '#b9a77f',
  hen: '#f2efe6',
  pig: '#e7b3a3',
  sheep: '#efe9dc',
  cow: '#3a3a36',
  cowPatch: '#efe9dc',
  appleLeaf: '#5e9550',
  apple: '#c0392b',
  strawberryLeaf: '#4f8a3f',
  strawberry: '#c7372b',
  louvre: '#3d4248',
  cladding: '#d9dcdd',
  claddingDark: '#7d858c',
  vapour: '#f3f5f6',
  insulator: '#cfd9de',
  copper: '#b87333',
  fieldA: '#a9c293',
  fieldB: '#c9b25e',
  fieldC: '#7fae6c',
  fieldD: '#8fa86a',
  carPark: '#6a6d72',
  mountain: '#7d8ea3',
  snow: '#f2f4f6',
  brick: '#a5523a',
  terrain: '#ffffff',
  spire: '#c9c2b2',
  hive: '#efe7d3',
  hiveRoof: '#c98a1b',
  glasshouse: '#cfe3ea',
  tomato: '#d0452e',
  hop: '#7fae6c',
  vine: '#5d7f3a',
  grape: '#5b3a6b',
  saffron: '#8a5ab4',
  cress: '#7fae6c',
  pond: '#6b8fa3',
} as const satisfies Record<string, string>;
